import { RunContextApp } from "framework/RCApp";
import {
  CompareAttributes,
  CompareClusters,
  CompareSheets,
  StageAttribute,
  UnstageAttribute,
  StageCluster,
  UnstageCluster,
  UnstageSelection,
  UpdateAttribute,
  UpdateCluster,
  CreateAttribute,
  CreateCluster,
  CreateBundle,
  ImportSheets,
  StageSheetRows,
  GetStagedSheets,
  SyncClustersDb
} from "types/apis/dcxPromotionInterfaces";

export interface DcxPromotionApiResponse<T> {
  success: boolean;
  data?: T;
  error?: any;
}

/** Filename the server picked, out of Content-Disposition; falls back to a generated one. */
const bundleFilename = (headers: any, projectId: string): string => {
  const raw =
    headers?.['content-disposition'] ??
    headers?.['Content-Disposition'] ??
    (typeof headers?.get === 'function' ? headers.get('content-disposition') : null);
  const match = typeof raw === 'string' ? raw.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i) : null;
  return match?.[1] ?? `dcx-promotion-bundle-${projectId}.zip`;
};

export const DCXPromotionApis = {
  /**
   * Compare attributes between two pilot/project environments (current_env -> target_env)
   */
  compareAttributes: async (
    rc: RunContextApp,
    projectId: string,
    currentEnv: string,
    targetEnv: string
  ): Promise<DcxPromotionApiResponse<CompareAttributes.retval>> => {
    try {
      const response = await rc.bffClient.compareDcxPromotionAttributes(projectId, currentEnv, targetEnv);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData ?? { targetEnvironments: [], data: [] } };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> GET request to : compareAttributes for ${currentEnv} -> ${targetEnv}\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Compare clusters between two pilot/project environments (current_env -> target_env)
   */
  compareClusters: async (
    rc: RunContextApp,
    projectId: string,
    currentEnv: string,
    targetEnv: string
  ): Promise<DcxPromotionApiResponse<CompareClusters.retval>> => {
    try {
      const response = await rc.bffClient.compareDcxPromotionClusters(projectId, currentEnv, targetEnv);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData ?? { targetEnvironments: [], data: [] } };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> GET request to : compareClusters for ${currentEnv} -> ${targetEnv}\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Compare DCX feature-metric resolutions (the "DCX Matrix"/sheet) between two
   * environments (current_env -> target_env). Read-only — no staging for sheets.
   */
  compareSheets: async (
    rc: RunContextApp,
    projectId: string,
    currentEnv: string,
    targetEnv: string,
    level?: 'LEVEL_1' | 'LEVEL_2'
  ): Promise<DcxPromotionApiResponse<CompareSheets.retval>> => {
    try {
      const response = await rc.bffClient.compareDcxPromotionSheets(projectId, currentEnv, targetEnv, level);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData ?? { targetEnvironments: [], data: [] } };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> GET request to : compareSheets for ${currentEnv} -> ${targetEnv}\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Stage an attribute value change for a target environment (does not write through to the external API)
   */
  stageAttribute: async (
    rc: RunContextApp,
    requestData: StageAttribute.params
  ): Promise<DcxPromotionApiResponse<StageAttribute.retval>> => {
    try {
      const response = await rc.bffClient.stageDcxPromotionAttribute(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : stageAttribute\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Undo a previously staged attribute change
   */
  unstageAttribute: async (
    rc: RunContextApp,
    targetEnv: string,
    projectId: string,
    attributeId: string
  ): Promise<DcxPromotionApiResponse<UnstageAttribute.retval>> => {
    try {
      const response = await rc.bffClient.unstageDcxPromotionAttribute(targetEnv, projectId, attributeId);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> DELETE request to : unstageAttribute for target ${targetEnv}, attribute ${attributeId}\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Stage a cluster evaluationCriteria change for a target environment (does not write through to the external API)
   */
  stageCluster: async (
    rc: RunContextApp,
    requestData: StageCluster.params
  ): Promise<DcxPromotionApiResponse<StageCluster.retval>> => {
    try {
      const response = await rc.bffClient.stageDcxPromotionCluster(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : stageCluster\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Undo a previously staged cluster change
   */
  unstageCluster: async (
    rc: RunContextApp,
    targetEnv: string,
    projectId: string,
    clusterReference: string
  ): Promise<DcxPromotionApiResponse<UnstageCluster.retval>> => {
    try {
      const response = await rc.bffClient.unstageDcxPromotionCluster(targetEnv, projectId, clusterReference);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> DELETE request to : unstageCluster for target ${targetEnv}, cluster ${clusterReference}\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Drop staged changes for one environment+pilot in a single call — attributes, clusters
   * and/or sheet rows. Only what the selection names is removed; an empty selection is a 400
   * server-side, and naming a `type` alongside that same type's ids is too.
   */
  unstageSelection: async (
    rc: RunContextApp,
    requestData: UnstageSelection.params
  ): Promise<DcxPromotionApiResponse<UnstageSelection.retval>> => {
    try {
      const response = await rc.bffClient.unstageDcxPromotionSelection(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : /v1/dcx-promotion/unstage\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Direct write of an attribute value into a target environment — Sync for uat/dev/productqa
   * targets, no staging step. Clears any staged row for (target_env, attributeId) on success.
   */
  updateAttribute: async (
    rc: RunContextApp,
    requestData: UpdateAttribute.params
  ): Promise<DcxPromotionApiResponse<UpdateAttribute.retval>> => {
    try {
      const response = await rc.bffClient.updateDcxPromotionAttribute(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> PUT request to : updateAttribute\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Direct write of a cluster's evaluationCriteria into a target environment — Sync for
   * uat/dev/productqa targets, no staging step. Clears any staged row on success.
   */
  updateCluster: async (
    rc: RunContextApp,
    requestData: UpdateCluster.params
  ): Promise<DcxPromotionApiResponse<UpdateCluster.retval>> => {
    try {
      const response = await rc.bffClient.updateDcxPromotionCluster(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> PUT request to : updateCluster\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Create an attribute in a target environment — for NOT_PRESENT diff items, where the
   * direct-write update endpoint cannot create. Clears any staged row on success.
   */
  createAttribute: async (
    rc: RunContextApp,
    requestData: CreateAttribute.params
  ): Promise<DcxPromotionApiResponse<CreateAttribute.retval>> => {
    try {
      const response = await rc.bffClient.createDcxPromotionAttribute(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : createAttribute\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Create a VIRTUAL cluster in a target environment — for NOT_PRESENT diff items. Returns
   * the new clusterId. Clears any staged row on success.
   */
  /**
   * Import Access Matrix — replace the target environment's whole feature access matrix with
   * the source environment's. Staged for nonprod/prod targets, written through otherwise;
   * `mode` in the response says which happened.
   */
  importSheets: async (
    rc: RunContextApp,
    requestData: ImportSheets.params
  ): Promise<DcxPromotionApiResponse<ImportSheets.retval>> => {
    try {
      const response = await rc.bffClient.importDcxPromotionSheets(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const body = ('data' in response && response.data) ? response.data as ImportSheets.retval : null;
      if (!body) {
        return { success: false, error: 'Invalid import response from server' };
      }
      if (body.success === false) {
        return { success: false, error: (body as any).error || 'Failed to import the access matrix' };
      }

      return { success: true, data: body };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : /v1/dcx-promotion/sheets/import\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Read back what's staged for a target env — used to mark already-staged cells purple in
   * the Feature Access / Template Personalization grids (those grids' own fetch doesn't
   * overlay staging).
   */
  getStagedSheets: async (
    rc: RunContextApp,
    params: GetStagedSheets.params
  ): Promise<DcxPromotionApiResponse<GetStagedSheets.retval>> => {
    try {
      const response = await rc.bffClient.getDcxPromotionStagedSheets(params);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData ?? { target_env: params.target_env, count: 0, data: [] } };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> GET request to : getStagedSheets\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Edit staged sheet rows in bulk (nonprod/prod target) — the whole grid in one transaction.
   * Update-only: rows that aren't staged come back under `rows.notStaged` rather than failing
   * the batch, and mean "this environment has no imported matrix yet, run Import Sheet first".
   */
  stageSheetRows: async (
    rc: RunContextApp,
    requestData: StageSheetRows.params
  ): Promise<DcxPromotionApiResponse<StageSheetRows.retval>> => {
    try {
      const response = await rc.bffClient.stageDcxPromotionSheetRows(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> PUT request to : stageSheetRows\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Shell bundle for the selected diff rows — POST /v1/dcx-promotion/create-bundle.
   * Responds with a binary zip, so this returns the blob plus the server's filename.
   */
  createBundle: async (
    rc: RunContextApp,
    requestData: CreateBundle.params
  ): Promise<DcxPromotionApiResponse<CreateBundle.retval>> => {
    try {
      const response = await rc.bffClient.createDcxPromotionBundle(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const blob = ('data' in response ? response.data : null) as Blob | null;
      if (!blob) {
        return { success: false, error: 'Empty bundle response from server' };
      }

      return {
        success: true,
        data: { blob, filename: bundleFilename('headers' in response ? response.headers : null, requestData.project_id) },
      };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : /v1/dcx-promotion/create-bundle\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  createCluster: async (
    rc: RunContextApp,
    requestData: CreateCluster.params
  ): Promise<DcxPromotionApiResponse<CreateCluster.retval>> => {
    try {
      const response = await rc.bffClient.createDcxPromotionCluster(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : createCluster\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  },

  /**
   * Backfill the local da_dcx_cluster cache for a pilot from ab-experiment — not a promotion
   * write. Intended to be called in the background (e.g. on cluster list load); safe to call
   * repeatedly, a no-op when nothing is missing.
   */
  syncClustersDb: async (
    rc: RunContextApp,
    requestData: SyncClustersDb.params
  ): Promise<DcxPromotionApiResponse<SyncClustersDb.retval>> => {
    try {
      const response = await rc.bffClient.syncDcxPromotionClustersDb(requestData);

      if ('error' in response && response.error) {
        return { success: false, error: response.error };
      }

      const responseData = ('data' in response && response.data) ? response.data : null;
      return { success: true, data: responseData?.data ?? responseData };
    } catch (error) {
      rc.error(rc.getName(DCXPromotionApis),
        `Error >> POST request to : syncClustersDb\n` +
        `\nresulted in error : \n`, error);

      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  }
};
